import { Game } from "../Game";
import { Pool } from "../Utils/Collections/Pool";
import { WrappedArray } from "../Utils/Collections/WrapperArray";
import { TIMER } from "./Gameplay";



class CircleFxEntity {
    private _fx: CircleFx;
    private _entity: pc.Entity;
    private _actTime: number;

    get isActive(): boolean { return this._actTime >= 0; }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(fx: CircleFx) {
        this._fx = fx;

        let settings = fx.settings;

        // Vytvorit entitu
        this._entity = new pc.Entity();
        this._entity.addComponent(pc.eCompomentType.model, {
            type: pc.eModelType.plane,
            castShadows: false,
            castShadowsLightmap: false,
            receiveShadows: settings.receiveShadows,
            material: CircleFx.material,
            layers: [settings.layerId]
        });

        settings.parent.addChild(this._entity);

        this.reset();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): void {
        this._actTime = -1;
        this._entity.model.meshInstances[0].visible = false;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    activate(): void {
        let settings = this._fx.settings;
        let circleSettings = settings.circleSettings;
        let animSettings = settings.animSettings;
        let scale = animSettings.scaleFrom;

        // Nastavit entitu
        let mesh = this._entity.model.meshInstances[0];
        let color = circleSettings.color;

        mesh.setParameter("uInnerRadius", circleSettings.innerRadius);
        mesh.setParameter("uInnerGradientSize", circleSettings.innerGradientSize);
        mesh.setParameter("uOuterGradientSize", circleSettings.outerGradientSize);
        mesh.setParameter("material_emissive", [color.r, color.g, color.b]);
        mesh.setParameter("material_opacity", animSettings.opacityFrom * settings.opacity);
        mesh.drawOrder = settings.drawOrder;

        if (settings.rotation)
            this._entity.setEulerAngles(settings.rotation);

        this._entity.setLocalPosition(settings.pos);
        this._entity.setLocalScale(scale, scale, scale);

        // Zobrazit
        mesh.visible = true;

        // Ulozit cas aktivace
        this._actTime = TIMER.time;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): boolean {
        let animSettings = this._fx.settings.animSettings;
        let progress = (TIMER.time - this._actTime) / animSettings.duration;

        if (progress >= 1) {
            this.reset();
            return false;
        }

        // Scale
        let scale = animSettings.scaleFrom + (animSettings.scaleTo - animSettings.scaleFrom) * animSettings.scaleEase(progress);

        this._entity.setLocalScale(scale, scale, scale);

        // Opacity
        this._entity.model.meshInstances[0].setParameter("material_opacity", (animSettings.opacityFrom + (animSettings.opacityTo - animSettings.opacityFrom) * animSettings.opacityEase(progress)) * this._fx.settings.opacity);

        return true;
    }
}



export interface CircleFxAnimSettings {
    /**
     * Delka animace v ms
     */
    duration: number;

    scaleFrom: number;
    scaleTo: number;
    scaleEase: (k: number) => number;

    opacityFrom: number;
    opacityTo: number;
    opacityEase: (k: number) => number;
}



export interface CircleFxCircleSettings {
    /**
     * Polomer vnitrniho vyrezu. Celkovy polomer kruhu je 0.5
     */
    innerRadius: number;
    /**
     * Velikost prechodu z prazdna do plna pro vnitrni okraj
     */
    innerGradientSize: number;
    /**
     * Velikost prechodu z plna do prazdna pro vnejsi okraj
     */
    outerGradientSize: number;
    /**
     * Barva plne casti kruhu
     */
    color: pc.Color;
}



export class CircleFxSettings {
    /**
     * (RO) Rodicovsky pc.GraphNode
     */
    get parent(): pc.GraphNode { return this._parent; }
    private _parent: pc.GraphNode;
    /**
     * (RO) Index vrstvy
     */
    get layerId(): number { return this._layerId; }
    private _layerId: number;
    /**
     * Lokalni pozice v parent
     */
    pos: pc.Vec3;
    /**
     * Celkova opacity efektu
     */
    opacity: number;
    /**
     * Interval aktivace kruhu
     */
    interval: number;
    /**
     * (RO) True, pokud ma efekt prijimat stin
     */
    get receiveShadows(): boolean { return this._receiveShadows; }
    private _receiveShadows: boolean;
    /**
     * Rotace efektu ve stupnich. Zakladni efekt je nalezato
     */
    rotation: pc.Vec3;
    /**
     * Mesehe s vyssim drawOrder se vykresluji nad meshi s nizsim drawOrder. Default = 0
     */
    drawOrder: number;
    /**
     * Nastaveni animace
     */
    animSettings: CircleFxAnimSettings;
    /**
     * Nastaveni vzhledu vytvarenych kruhu
     */
    circleSettings: CircleFxCircleSettings;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(circleSettings: CircleFxCircleSettings, animSettings: CircleFxAnimSettings, interval: number, opacity: number = 1, parent?: pc.GraphNode, pos: pc.Vec3 = new pc.Vec3(), rotation: pc.Vec3 = new pc.Vec3(), drawOrder: number = 0, layerId?: number, receiveShadows: boolean = false) {
        this.circleSettings = circleSettings;
        this.animSettings = animSettings;
        this.interval = interval;
        this.opacity = opacity;
        this._parent = parent || Game.app.root;
        this.pos = pos;
        this.rotation = rotation;
        this.drawOrder = drawOrder;
        this._layerId = layerId || Game.app.scene.layers.getLayerByName("World").id;
        this._receiveShadows = receiveShadows;
    }
}



export class CircleFx {
    /**
     * Nastaveni efektu
     */
    settings: CircleFxSettings;
    /**
     * Cas, kdy ma dojit k ativaci dalsiho kruhu
     */
    private _nextEntityTime: number;

    private _actEntities: WrappedArray<CircleFxEntity>;
    private _freeEntities: Pool<CircleFxEntity>;

    /**
     * (RO) Material pro vykresleni kruhu.
     * Parametery pro mesh:
     * material_emissive - barva kruhu [r,g,b]
     * uInnerRadius - polomer vnitrniho vyrezu (plny kruh ma polomer 0.5)
     * uInnerGradientSize - velikost prechodu z prazdna do plna (plny kruh ma polomer 0.5)
     * uOuterGradientSize - velikost prechodu z plna do prazdna (plny kruh ma polomer 0.5)
     */
    static get material(): pc.StandardMaterial { return CircleFx._material; }
    private static _material: pc.StandardMaterial;

    get isActive(): boolean { return this._isActive; }
    private _isActive: boolean;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(settings: CircleFxSettings) {
        // Vytvorit material
        if (!CircleFx._material)
            this.createMaterial();

        // Ulozit nastaveni
        this.settings = settings;

        // Pool s entitami vytvrajici kruhy
        this._freeEntities = new Pool<CircleFxEntity>(undefined, 1, true, (): CircleFxEntity => {
            return new CircleFxEntity(this);
        });
        // Seznam aktivnich entit
        this._actEntities = new WrappedArray<CircleFxEntity>();

        // Vypnout efekt
        this.reset();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): void {
        // Uvolnit entity
        this._actEntities.forEach((ent: CircleFxEntity): void => {
            ent.reset();
            this._freeEntities.returnItem(ent);
        });
        this._actEntities.clear();

        // Vypnout efekt
        this._nextEntityTime = -1;
        this._isActive = false;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    start(): void {
        this._nextEntityTime = TIMER.time;
        this._isActive = true;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    stop(): void {
        // Zastavit vytvareni novych kruhu
        this._nextEntityTime = -1;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): void {
        if (!this._isActive)
            return;

        // Zpracovat aktivni kruhy
        this._actEntities.forEach((circle: CircleFxEntity): void => { circle.update(); });

        // Vyradit neaktivni
        while (!this._actEntities.empty && !this._actEntities.first.isActive) {
            this._freeEntities.returnItem(this._actEntities.first);
            this._actEntities.remove(false);

            if (this._actEntities.empty) {
                this.reset();
                return;
            }
        }

        // Aktivace nove entity
        if (this._nextEntityTime >= 0 && this._nextEntityTime <= TIMER.time) {
            let ent = this._freeEntities.getItem();

            ent.activate();
            this._actEntities.add(ent);

            this._nextEntityTime += this.settings.interval;
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    private createMaterial(): void {
        const opacityChunk: string =
            "uniform float material_opacity;\n" +
            "uniform float uInnerRadius;\n" +
            "uniform float uInnerGradientSize;\n" +
            "uniform float uOuterGradientSize;\n" +

            "void getOpacity() {\n" +
            "   float len = length($UV.xy - vec2(0.5));\n" +
            "   float inCircle = (1.0 - smoothstep(0.5 - uOuterGradientSize, 0.5, len)) * smoothstep(uInnerRadius, uInnerRadius + uInnerGradientSize, len);\n" +

            "   dAlpha = material_opacity * inCircle; }";

        let mat = CircleFx._material = new pc.StandardMaterial();

        mat.diffuse.set(0, 0, 0);
        mat.emissive.set(1, 1, 1);
        mat.blendType = pc.BLEND_NORMAL;
        mat.depthWrite = false;
        mat.useLighting = false;
        mat.useGammaTonemap = false;
        mat.opacity = 0.99;
        mat.opacityMap = Game.atlas.texture; // Musim nastavit texturu, aby se predavalo UV do shader chunku
        mat.chunks.opacityPS = opacityChunk;
        mat.update();
    }
}
